// Backend · Alerts — configurable alert RULES + triggered-alert management.
//   • Triggered — active + history, grouped by production/category, ack/resolve
//   • Rules     — create/edit rules (system + backfill-quality; pacing/ROAS soon)
//   • Channels  — the webhook/email registry rules deliver through
// Reads: useAlerts (30s poll), useAlertRules, useNotificationChannels.
//
// Each tab and drawer lives in its own `backend-alerts-*.tsx` sibling, and the
// pure activation, validation and label helpers in `lib/alert-*.ts`. This file
// keeps the screen shell and re-exports what routes and tests import.

import { PageActions } from '@/components/page-actions';
import { useRef, useState, type KeyboardEvent } from 'react';
import { QueryErrorNote } from '@/components/error-panel';
import { useCan } from '@/auth/capabilities';
import { useAlerts, useAlertRules, useNotificationChannels } from '@/api/hooks';
import type { AlertMetricCatalogEntry, AlertRule, AlertRulesResponse, NotificationChannel } from '@/api/types';
import {
  activationHelpForRule,
  activationModeOf,
  activationOptionsForRule,
} from '@/lib/alert-activation';
import { ActivationSelector } from './backend-alerts-activation';
import { MetricExplainerHint } from './backend-alerts-parts';
import { TriggeredTab } from './backend-alerts-triggered';
import { RulesTab } from './backend-alerts-rules';
import { ChannelsTab } from './backend-alerts-channels';
import { AlertRuleDrawer } from './backend-alerts-rule-drawer';
import { ProtectedRuleDrawer } from './backend-alerts-protected-drawer';
import { ChannelDrawer } from './backend-alerts-channel-drawer';

type AlertsTab = 'triggered' | 'rules' | 'channels';

const NO_RULES: readonly AlertRule[] = [];
const NO_CATALOG: readonly AlertMetricCatalogEntry[] = [];
const NO_CHANNELS: readonly NotificationChannel[] = [];
const NO_EXPLAINERS: AlertRulesResponse['explainers'] = {};

function AlertsScreen({ incidentId = null }: { incidentId?: string | null }) {
  const [tab, setTab] = useState<AlertsTab>('triggered');
  const canEdit = useCan('backend_edit');

  const alertsQ = useAlerts(false);
  const rulesQ = useAlertRules();
  const channelsQ = useNotificationChannels();
  const alerts = alertsQ.data;
  const rules = rulesQ.data?.rules ?? NO_RULES;
  const metricCatalog = rulesQ.data?.metric_catalog ?? NO_CATALOG;
  const explainers = rulesQ.data?.explainers ?? NO_EXPLAINERS;
  const channels = channelsQ.data?.channels ?? NO_CHANNELS;
  const activeQuery = tab === 'triggered' ? alertsQ : tab === 'rules' ? rulesQ : channelsQ;
  const ruleCatalogReady = metricCatalog.length > 0;

  const [ruleEditing, setRuleEditing] = useState<AlertRule | 'new' | null>(null);
  const [channelEditing, setChannelEditing] = useState<NotificationChannel | 'new' | null>(null);
  const tabs: readonly (readonly [AlertsTab, string])[] = [
    ['triggered', 'Triggered'],
    ['rules', `Rules (${rules.length})`],
    ['channels', `Channels (${channels.length})`],
  ];
  // Tabs, not buttons: one tab stop, the arrows, Home and End move between
  // them and select as they go (automatic activation, like the report tabs).
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number | null = null;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    const target = next === null ? undefined : tabs[next];
    if (next === null || !target) return;
    event.preventDefault();
    setTab(target[0]);
    tabRefs.current[next]?.focus();
  };

  return (
    <div className="tbl-frame">
      <div className="page-hd">
        <div><h1 className="page-title">Alerts</h1></div>
        <PageActions style={{ gap: 8, alignItems: 'center' }}>
          <span className="muted mono" data-phone="meta" style={{ fontSize: 11 }}>
            {alerts ? alerts.active_count : '—'} active
          </span>
          {tab === 'rules' && canEdit && (
            <button className="btn btn-primary" data-phone="inline" disabled={!ruleCatalogReady} title={!ruleCatalogReady ? 'Waiting for the server rule catalog' : undefined} onClick={() => setRuleEditing('new')}>+ New rule</button>
          )}
          {tab === 'channels' && canEdit && (
            <button className="btn btn-primary" data-phone="inline" onClick={() => setChannelEditing('new')}>+ New channel</button>
          )}
        </PageActions>
      </div>

      <div className="log-view-tabs" role="tablist" aria-label="Alert views" style={{ display: 'flex', gap: 4, padding: '8px 22px 0', borderBottom: '1px solid var(--line)', background: 'var(--bg-elev)' }}>
        {tabs.map(([id, label], index) => (
          <button
            key={id}
            ref={(node) => { tabRefs.current[index] = node; }}
            type="button"
            role="tab"
            id={`alerts-tab-${id}`}
            aria-controls="alerts-tabpanel"
            aria-selected={tab === id}
            aria-keyshortcuts="ArrowLeft ArrowRight Home End"
            tabIndex={tab === id ? 0 : -1}
            onClick={() => setTab(id)}
            onKeyDown={(event) => onTabKeyDown(event, index)}
            style={{ padding: '8px 16px', cursor: 'pointer', background: 'transparent', border: 'none',
              borderBottom: '2px solid ' + (tab === id ? 'var(--accent, var(--ink-1))' : 'transparent'),
              color: tab === id ? 'var(--ink-1)' : 'var(--ink-3)', fontWeight: tab === id ? 700 : 500, fontSize: 13 }}>
            {label}
          </button>
        ))}
      </div>

      {/* The panel is the flex column the tab's parts used to sit in directly
          (`.tbl-frame`), so the strip, linked incident and lists keep their
          sizing (B20). */}
      <div id="alerts-tabpanel" role="tabpanel" aria-labelledby={`alerts-tab-${tab}`} className="alert-tabpanel">
        {tab !== 'triggered' && activeQuery.isLoading && (
          <div className="muted" style={{ padding: 40, textAlign: 'center', fontSize: 12 }}>
            Loading {tab}…
          </div>
        )}
        {tab !== 'triggered' && activeQuery.isError && (
          <QueryErrorNote
            error={activeQuery.error}
            what={tab}
            onRetry={() => void activeQuery.refetch()}
          />
        )}

        {tab === 'triggered' && (
          <TriggeredTab alerts={alerts?.alerts ?? []} statusCounts={alerts?.status_counts} resolvedCursor={alerts?.resolved_cursor ?? null} alertsQuery={alertsQ} incidentId={incidentId} />
        )}
        {!activeQuery.isLoading && !activeQuery.isError && tab === 'rules' && (
          <RulesTab rules={rules} metricCatalog={metricCatalog} explainers={explainers} canEdit={canEdit} onEdit={setRuleEditing} />
        )}
        {!activeQuery.isLoading && !activeQuery.isError && tab === 'channels' && (
          <ChannelsTab channels={channels} canEdit={canEdit} onEdit={setChannelEditing} />
        )}
      </div>

      {ruleEditing && ruleEditing !== 'new' && ruleEditing.protected ? (
        // Protected definitions stay fixed. Activation is always editable;
        // system metrics additionally expose their two narrow thresholds.
        <ProtectedRuleDrawer
          rule={ruleEditing}
          metricCatalog={metricCatalog}
          explainer={explainers[ruleEditing.id]}
          onClose={() => setRuleEditing(null)}
        />
      ) : ruleEditing ? (
        <AlertRuleDrawer
          rule={ruleEditing === 'new' ? null : ruleEditing}
          explainer={ruleEditing === 'new' ? null : explainers[ruleEditing.id]}
          channels={channels}
          metricCatalog={metricCatalog}
          availableCategories={rulesQ.data?.available_categories}
          editorDefaults={rulesQ.data?.editor_defaults}
          onClose={() => setRuleEditing(null)}
        />
      ) : null}
      {channelEditing && (
        <ChannelDrawer channel={channelEditing === 'new' ? null : channelEditing} rules={rulesQ.data ? rules : undefined} onClose={() => setChannelEditing(null)} />
      )}
    </div>
  );
}

export {
  MetricExplainerHint,
  ActivationSelector,
  AlertsScreen,
  activationHelpForRule,
  activationModeOf,
  activationOptionsForRule,
};
